import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import { Network } from 'data/network/network.types';
import * as networkSelectors from 'data/network/network.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/network/network.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import { FieldUpdateHandlerParam } from 'shared/form';
import FormDialog from 'components/FormDialog';
import { RetrievingDataProgress } from 'components';
import NetworkForm, { NetworkFormRef } from '../../components/NetworkForm';
import { networkTemplate } from '../../templates/networkTemplate';
import { createNetworkBasedOnTemplate } from '../../templates/networkTemplate.factory';
import getNetworkDataToSubmit from '../../utils/getNetworkDataToSubmit';

const mapState = (state: RootState) => ({
  isOpen: networkSelectors.isNetworkCreatePopupOpen(state),
  isLoading: networkSelectors.isNetworkLoading(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  network: networkSelectors.getNetwork(state),
});

const mapDispatch = {
  createNetwork: actions.createNetworkFromForm,
  closeNetworkCreatePopup: actions.closeNetworkCreatePopup,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;

export type Props = PropsFromRedux;

export const NetworkCreateDialog: FunctionComponent<Props> = ({
  isOpen,
  isLoading,
  deliveryFunctionId,
  isAuditEnabled,
  userId,
  userName,
  network,
  createNetwork,
  closeNetworkCreatePopup,
}: Props) => {
  const [template, setTemplate] = useState(networkTemplate);
  const [formData, setFormData] = useState<Partial<Network>>({});
  const networkFormRef = useRef<NetworkFormRef>(null);

  useEffect(() => {
    setFormData({
      ...createNetworkBasedOnTemplate(networkTemplate),
      ...network,
    });

    const clonedTemplate = cloneDeep(networkTemplate);
    clonedTemplate.formId = Date.now().toString();
    setTemplate(clonedTemplate);
  }, [isOpen, network]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const handleClickSave = () => {
    const isValid = networkFormRef?.current?.isValid(true, true) ?? true;
    if (!isValid) {
      return;
    }

    const formValues = networkFormRef?.current?.getValues();
    if (!formValues) {
      return;
    }

    const body = getNetworkDataToSubmit(
      formValues,
      null,
      isAuditEnabled,
      userId,
      userName,
      'CREATE',
    );

    createNetwork(deliveryFunctionId, body);
  };

  const handleClickCancel = () => closeNetworkCreatePopup();
  const { t } = useTranslation();

  return (
    <div data-testid="NetworkCreateDialog">
      <FormDialog
        isOpen={isOpen}
        title={t('network.dialog.title')}
        onSaveClick={handleClickSave}
        onCancelClick={handleClickCancel}
      >
        <NetworkForm
          data={formData}
          template={template}
          ref={networkFormRef}
          onFieldUpdate={handleFieldUpdate}
        />
      </FormDialog>
      <RetrievingDataProgress isOpen={isLoading} message={t('network.dialog.saving.message')} />
    </div>
  );
};

export default connector(NetworkCreateDialog);
